// Gera 2-3 exemplos de JSON de entrada relevantes para uma regra,
// extraindo os caminhos referenciados (cliente.uf, pedido.valor, etc) e
// criando payloads que casam diferentes ramos da regra.

function setPath(obj, path, valor) {
  if (!path) return;
  const partes = String(path).split(".");
  let atual = obj;
  for (let i = 0; i < partes.length - 1; i++) {
    if (!atual[partes[i]] || typeof atual[partes[i]] !== "object") {
      atual[partes[i]] = {};
    }
    atual = atual[partes[i]];
  }
  atual[partes[partes.length - 1]] = valor;
}

// Coleta todos os caminhos usados na regra (condições, expressões, mapeamentos)
function coletarCaminhos(regra) {
  const caminhos = new Set();
  if (!regra || !regra.tipo) return [];

  function visitarExpressao(expr) {
    if (!expr || typeof expr !== "object") return;
    if (expr.tipo === "campo" && expr.caminho) caminhos.add(expr.caminho);
  }

  function visitarSaida(saida) {
    if (Array.isArray(saida) && saida[0] && "expressao" in saida[0]) {
      saida.forEach((s) => visitarExpressao(s.expressao));
    } else {
      visitarExpressao(saida);
    }
  }

  if (regra.tipo === "condicional") {
    (regra.casos || []).forEach((caso) => {
      ((caso.condicao && caso.condicao.grupos) || []).forEach((g) => {
        (g.condicoes || []).forEach((c) => {
          if (c.campo) caminhos.add(c.campo);
          if (c.tipo_valor === "campo" && c.valor) caminhos.add(c.valor);
        });
      });
      visitarSaida(caso.entao);
    });
    visitarSaida(regra.senao);
  } else if (regra.tipo === "coalesce") {
    (regra.campos || []).forEach((c) => c && caminhos.add(c));
  } else if (regra.tipo === "concatenacao") {
    (regra.partes || []).forEach(visitarExpressao);
  } else if (regra.tipo === "mapeamento") {
    if (regra.campo) caminhos.add(regra.campo);
  }

  return [...caminhos].filter(Boolean);
}

// Valor de exemplo plausível baseado no nome do campo
function exemploValor(caminho) {
  const ultimo = String(caminho).split(".").pop().toLowerCase();
  if (ultimo === "uf" || ultimo === "estado") return "SP";
  if (ultimo === "cidade") return "São Paulo";
  if (ultimo.includes("cep")) return "01310-100";
  if (ultimo.includes("cnpj")) return "12.345.678/0001-90";
  if (ultimo.includes("cpf")) return "123.456.789-00";
  if (ultimo.includes("email")) return "cliente@exemplo.com";
  if (ultimo.includes("telefone") || ultimo.includes("celular")) return "(11) 99999-9999";
  if (ultimo === "tipo") return 1;
  if (ultimo === "id" || ultimo.endsWith("_id")) return 42;
  if (ultimo.includes("valor") || ultimo.includes("total") || ultimo.includes("preco")) return 1500;
  if (ultimo === "quantidade" || ultimo === "qtd") return 10;
  if (ultimo === "ativo" || ultimo.startsWith("is_") || ultimo.startsWith("flag_")) return true;
  if (ultimo === "nome" || ultimo === "razao_social") return "Cliente Exemplo Ltda";
  if (ultimo === "data" || ultimo.includes("_at") || ultimo.includes("data_")) return "2026-01-15";
  return "valor_exemplo";
}

// Para condicional: gera um payload por caso + um para o fallback
function exemplosCondicional(regra) {
  const exemplos = [];
  const casos = regra.casos || [];

  casos.forEach((caso, idx) => {
    const payload = {};
    ((caso.condicao && caso.condicao.grupos) || []).forEach((g) => {
      (g.condicoes || []).forEach((c) => {
        if (!c.campo) return;
        let v;
        const op = c.operador || "igual";
        const ref = c.tipo_valor === "campo" ? null : c.valor;
        if (op === "nulo") v = null;
        else if (op === "nao_nulo") v = exemploValor(c.campo);
        else if (op === "em" || op === "nao_em") {
          try {
            // Aceita tanto array nativo quanto string JSON (compat com payloads antigos)
            const arr = typeof ref === "string" ? JSON.parse(ref) : Array.isArray(ref) ? ref : [ref];
            v = Array.isArray(arr) && arr.length ? arr[0] : exemploValor(c.campo);
          } catch (_) { v = exemploValor(c.campo); }
        } else if (op === "maior" || op === "maior_igual") {
          v = Number(ref) + 1;
        } else if (op === "menor" || op === "menor_igual") {
          v = Number(ref) - 1;
        } else if (op === "diferente") {
          v = String(ref) + "_x";
        } else {
          v = ref !== undefined && ref !== "" ? ref : exemploValor(c.campo);
        }
        setPath(payload, c.campo, v);
      });
    });
    exemplos.push({
      label: `Regra ${idx + 1} casa`,
      payload,
    });
  });

  // Exemplo de fallback: usa caminhos referenciados mas com valores que NÃO casam
  if (casos.length > 0) {
    const payloadFallback = {};
    coletarCaminhos(regra).forEach((c) => {
      setPath(payloadFallback, c, "valor_que_nao_casa");
    });
    exemplos.push({ label: "Nenhuma regra casa (fallback)", payload: payloadFallback });
  }

  return exemplos.slice(0, 4);
}

function exemplosCoalesce(regra) {
  const campos = (regra.campos || []).filter(Boolean);
  if (campos.length === 0) return [];
  const exemplos = [];
  exemplos.push({
    label: `Todos vazios → null`,
    payload: campos.reduce((acc, c) => { setPath(acc, c, null); return acc; }, {}),
  });
  if (campos[0]) {
    const p = {};
    setPath(p, campos[0], exemploValor(campos[0]));
    exemplos.push({ label: `Apenas #1 preenchido`, payload: p });
  }
  if (campos.length > 1 && campos[1]) {
    const p = {};
    setPath(p, campos[0], null);
    setPath(p, campos[1], exemploValor(campos[1]));
    exemplos.push({ label: `#1 vazio, #2 preenchido`, payload: p });
  }
  return exemplos;
}

function exemplosConcatenacao(regra) {
  const payload = {};
  const partes = regra.partes || [];
  partes.forEach((p) => {
    if (p && p.tipo === "campo" && p.caminho) setPath(payload, p.caminho, exemploValor(p.caminho));
  });
  return [{ label: "Todas as partes preenchidas", payload }];
}

function exemplosMapeamento(regra) {
  if (!regra.campo) return [];
  const exemplos = [];
  const chaves = Object.keys(regra.mapa || {});
  chaves.slice(0, 2).forEach((k) => {
    const p = {};
    setPath(p, regra.campo, isNaN(Number(k)) ? k : Number(k));
    exemplos.push({ label: `Chave "${k}" → ${regra.mapa[k]}`, payload: p });
  });
  const p = {};
  setPath(p, regra.campo, "VALOR_FORA_DO_MAPA");
  exemplos.push({ label: "Chave não mapeada → padrão", payload: p });
  return exemplos;
}

export function gerarExemplosParaRegra(regra) {
  if (!regra || !regra.tipo) return [];
  switch (regra.tipo) {
    case "condicional":  return exemplosCondicional(regra);
    case "coalesce":     return exemplosCoalesce(regra);
    case "concatenacao": return exemplosConcatenacao(regra);
    case "mapeamento":   return exemplosMapeamento(regra);
    default: return [];
  }
}

// Descreve em texto a estrutura esperada do JSON
export function descreverEstrutura(regra) {
  const caminhos = coletarCaminhos(regra);
  if (caminhos.length === 0) return "Configure pelo menos um campo na regra para ver exemplos.";
  return caminhos.join(", ");
}
